import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import Image from "next/image";

import { BsArrowRight } from "react-icons/bs";
import { Separator } from "@/components/ui/separator";
import { getNameInitals, useToastHelper } from "@/helpers/helperFunctions";
import { cn } from "@/lib/utils";
import { Label } from "@radix-ui/react-label";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Layout from "../components/Layouts/Layout";
import Meta from "../components/Layouts/Meta";
import Loader from "../components/Loader";
import { Twitter, Linkedin } from "lucide-react";
import { API_URL } from "../config";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import TermsAndConditions from "@/components/TermsAndConditions";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import { Copy, ExternalLink } from "lucide-react";
import { MdDone } from "react-icons/md";
import { GetStaticProps } from "next";
import {
  usePostUpdateSocials,
  usePostUpdateUserDescribtion,
  usePostUploadProfilePhoto,
} from "@/api/userApi";
import { useUserContext } from "@/contexts/userContext";
import axios from "axios";
import React from "react";

interface SettingsLayoutProps {
  children: React.ReactNode;
}
export const SettingsLayout = ({ children }: SettingsLayoutProps) => {
  const router = useRouter();
  const sidebarNavItems = [
    {
      title: "Profile",
      href: "/settings",
    },
    {
      title: "Trading preferences",
      href: "/user-preferences",
    },
    {
      title: "Change password",
      href: "/change-password",
    },
    // {
    //   title: "Strategy alert settings",
    //   href: "/notification-settings",
    // },
  ];
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:space-x-12 lg:space-y-0 ">
      <aside className="-mx-4 lg:w-1/5 hidden md:block ">
        <nav className="flex space-x-1 lg:flex-col lg:space-x-0 lg:space-y-1">
          {sidebarNavItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                buttonVariants({ variant: "ghost" }),
                "justify-start font-medium",
                router.pathname === item.href
                  ? "bg-muted hover:bg-muted"
                  : "hover:bg-transparent hover:underline"
              )}
            >
              {item.title}
            </Link>
          ))}
        </nav>
      </aside>

      <Select
        onValueChange={(val) =>
          router.push({ pathname: val, query: router.query })
        }
        value={router.pathname}
      >
        <SelectTrigger className="w-full md:hidden">
          <SelectValue placeholder={sidebarNavItems[0].title} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {sidebarNavItems.map((item: any) => {
              return (
                <>
                  <SelectItem value={item.href}>{item.title}</SelectItem>
                </>
              );
            })}
          </SelectGroup>
        </SelectContent>
      </Select>

      <div className="flex-1 lg:max-w-2xl">{children}</div>
    </div>
  );
};

interface IFormValues {
  description?: string;
}

const SettingsAndPreferences = () => {
  const { t } = useTranslation("settings");
  const { toast } = useToastHelper();

  const [twitter, setTwitter] = useState(String);
  const [linkedin, setLinkedin] = useState(String);
  const [userInfo, setUserInfo] = useState<any>(null);
  const { user } = useUserContext();
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const router = useRouter();
  const descriptionForm = useForm<IFormValues>({ mode: "onSubmit" });

  const { mutateAsync: updateUserDescribtion } = usePostUpdateUserDescribtion();
  const { mutateAsync: uploadProfilePhoto } = usePostUploadProfilePhoto();
  const { mutateAsync: updateSocial } = usePostUpdateSocials();
  const setAuthHeader = (token: string) => {
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user-info")!);
    if (user == null) {
      router.push({
        pathname: "/",
        query: {
          redirect: window.location.href,
          locale: router.query.locale || "en",
        },
      });
      return;
    } else {
      setUserInfo(user);
      setUserLoading(false);

      if (user.preference.socials != "" && user.preference.socials != null) {
        var social_parse = JSON.parse(user.preference.socials);

        setTwitter(social_parse["twitter"]);
        setLinkedin(social_parse["linkedin"]);
      }

      descriptionForm.setValue("description", user.user.description || "");
    }
  }, []);

  const updateDescription: SubmitHandler<IFormValues> = async (values) => {
    try {
      const { description } = values;
      if (!description?.length) {
        toast.error(t("Trade Profile is required."));
        return;
      }

      const res = await updateUserDescribtion({
        user_id: user?.user_id as string,
        description: encodeURIComponent(description),
      });

      if (res.status === 200) {
        const info = { ...userInfo };
        info.user.description = description;
        localStorage.setItem("user-info", JSON.stringify(info));
        toast.success("Trade profile updated successfully");
      } else {
        toast.error(res.data.detail || res.data.errorMsg);
      }
    } catch (e: any) {
      console.error("error ", e.message);
      toast.error(e.response?.data?.detail || "An error occurred");
    }
  };

  const onSelectProfileImage = async (files: File[]) => {
    try {
      if (!files[0]) return;

      if (Math.round(files[0].size / 1024) > 1024) {
        toast.error(t("File was larger than 1MB in file size."));
        return false;
      }

      const formData = new FormData();
      formData.append("file", files[0]);
      formData.append("user_id", userInfo.user_id);

      const res = await uploadProfilePhoto({
        user_id: userInfo.user_id,
        formData,
      });

      if (res.status === 200) {
        userInfo.user.profile_image = res.data.data;
        setUserInfo(userInfo);
        localStorage.setItem("user-info", JSON.stringify(userInfo));
        toast.success(
          t("Image uploaded successfully and will be visible shortly.")
        );
      } else {
        toast.error(res.data.detail || res.data.errorMsg);
      }
    } catch (e: any) {
      console.error("error", e.message);
      toast.error(e.response?.data?.detail || "An error occurred");
    }
  };

  const urlCopy = () => {
    navigator.clipboard.writeText(userInfo?.user_id);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1500);
  };
  const updateSocials = () => {
    let url = new URL("update_user_socials", API_URL);
    url.searchParams.append("user_id", userInfo.user_id);
    url.searchParams.append("twitter", twitter);
    url.searchParams.append("linkedin", linkedin);
    axios
      .post(url.toString(), {}, setAuthHeader(userInfo.token))
      .then((res) => {
        if (res.status == 200) {
          toast.success(res.data.message);
          setOpen(false);
          userInfo.preference.socials = JSON.stringify({
            twitter: twitter,
            linkedin: linkedin,
          });
          setUserInfo(userInfo);
          localStorage.setItem("user-info", JSON.stringify(userInfo));
        } else {
          toast.error(res.data.detail || res.data.errorMsg);
        }
      })
      .catch((e) => {
        console.error("error ", e.message);
        throw new Error(e.message);
      });
  };
  const [open, setOpen] = useState(false);
  const main = () => {
    return (
      <Card className="w-full  mx-auto">
        <div className="space-y-6 p-10 block">
          <h2 className="text-2xl font-bold tracking-tight">
            {t("head1")} {t("head2")}
          </h2>
          <Separator className="my-6" />
          <SettingsLayout>
            <div className="flex flex-col w-full gap-5">
              <div>
                <h3 className="text-lg font-medium">{t("Profile")}</h3>
              </div>
              <Separator />
              <div className="flex flex-row items-center">
                <div>
                  <input
                    autoComplete="off"
                    id="profile-image-upload-input"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e: any) => onSelectProfileImage(e.target.files)}
                  />
                  <Label
                    htmlFor="profile-image-upload-input"
                    className="flex flex-row cursor-pointer "
                  >
                    <Avatar className="w-10">
                      <AvatarImage
                        src={userInfo?.user.profile_image}
                        id="userImage"
                        alt="userImage"
                        className="cursor-pointer w-fit"
                      />
                      <AvatarFallback>
                        {getNameInitals(userInfo?.user)}
                      </AvatarFallback>
                    </Avatar>
                  </Label>
                </div>
                <div className="flex gap-2 ">
                  <div className="text-md ml-3">
                    {`${userInfo?.user.first_name} ${userInfo?.user.last_name}`}
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <ExternalLink
                          size={15}
                          className="cursor-pointer"
                          onClick={() =>
                            router.push(
                              `/profile/${userInfo?.user?.unique_key}`
                            )
                          }
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Access your public profile</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              <div>
                <div className="flex flex-col text-sm font-medium">
                  <div className="flex gap-2">
                    <div>
                      <b>{t("id")}</b>
                    </div>
                    <div>{userInfo?.user_id}</div>

                    {isCopied ? (
                      <MdDone size={15} />
                    ) : (
                      <Copy
                        className="cursor-pointer"
                        size={15}
                        onClick={() => urlCopy()}
                      />
                    )}
                  </div>
                  <div>
                    <b>Registered Email:</b> {userInfo?.user.email}{" "}
                  </div>

                  <div className="mt-5">
                    <h1>
                      <b>Socials</b>
                    </h1>
                    <Dialog open={open} onOpenChange={setOpen}>
                      <DialogTrigger>
                        <div className="flex mt-3">
                          <div>
                            <Image
                              src="/twitter.png"
                              height={20}
                              width={20}
                              alt="twitter logo"
                            />
                          </div>
                          <div className="ml-5">
                            <Image
                              src="/linkedin.png"
                              height={20}
                              width={20}
                              alt="twitter logo"
                            />
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Edit your social accounts</DialogTitle>
                          <DialogDescription>
                            <div className="mt-5">
                              <h1>
                                Twitter:{" "}
                                <span className="text-xs text-muted-foreground">
                                  (optional)
                                </span>
                              </h1>
                              <Input
                                className="mt-2"
                                value={twitter}
                                onChange={(e) => setTwitter(e.target.value)}
                              />
                            </div>
                            <div className="mt-4">
                              <h1>
                                Linkedin:{" "}
                                <span className="text-xs text-muted-foreground">
                                  (optional)
                                </span>
                              </h1>
                              <Input
                                className="mt-2"
                                value={linkedin}
                                onChange={(e) => setLinkedin(e.target.value)}
                              />
                            </div>
                            <div className="w-full flex mt-4 align-right justify-end">
                              <div className="text-right">
                                <Button
                                  className="text-right"
                                  onClick={() => updateSocials()}
                                >
                                  Update
                                </Button>
                              </div>
                            </div>
                          </DialogDescription>
                        </DialogHeader>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="mt-5"></div>
                  <Form {...descriptionForm}>
                    <form
                      onSubmit={descriptionForm.handleSubmit(updateDescription)}
                      className="w-full md:max-w-md font-medium"
                    >
                      <FormField
                        control={descriptionForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              <b>{t("Trader Description")}</b>
                            </FormLabel>
                            <FormControl>
                              <Input placeholder={t("title")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="font-medium mt-3 float-right gap-2"
                      >
                        {t("Update Description")}
                        <BsArrowRight />
                      </Button>
                    </form>
                  </Form>
                </div>
              </div>
              <div className="text-xs text-muted-foreground hidden">
                {t("linkTest")}
                <Button
                  variant="link"
                  className="px-1 text-xs text-muted-foreground underline"
                >
                  <TermsAndConditions />
                </Button>
                {/* <Link href={"https://www.mktdynamics.com/terms-and-conditions"}>
                  <span className="underline cursor-pointer px-1">
                    <span>{t("termsAndConditions")}</span>
                  </span>
                </Link> */}
                {t("and")}
                <Button
                  variant="link"
                  className="px-1 text-xs text-muted-foreground underline"
                >
                  <PrivacyPolicy />
                </Button>
              </div>
            </div>
          </SettingsLayout>
        </div>
      </Card>
    );
  };
  if (userLoading)
    return (
      <div className="fixed h-screen w-screen flex justify-center items-center">
        <Loader text="loading..." />
      </div>
    );
  return (
    <Layout
      meta={
        <Meta title={t("meta.title")} description={t("meta.description")} />
      }
      hideRiskWarning
    >
      {main()}
    </Layout>
  );
};
export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || "en", ["settings", "layout"])),
    },
    revalidate: 60, // Updates translations every 60 seconds (ISR)
  };
};
export default SettingsAndPreferences;
